import { useCallback, useRef, useEffect } from 'react';
import { isEqual, cloneDeep } from 'lodash-es';
import type { TSheetDataMap } from './useSheetData';
import type { ISheet } from '../components/ExcelSheetTabs';
import { multiSheetDataToExcelFile, IMultiSheetData } from '../utils';
import useCos from '../../components/QAInput/hooks/useCOS';
import { usePageLifecycle } from '../../ChatDSTemplate/hooks';

interface IUseAutoSaveProps {
  /** 所有 sheet 数据 Map */
  sheetDataMap: TSheetDataMap;
  /** 所有 sheet 信息列表 */
  sheets: ISheet[];
  /** 当前 sheet 名称 */
  sheetName: string;
  /** 当前文件名 */
  currentFileName: string;
  /** 当前文件的 COS URL */
  currentFileCosUrl: string;
  /** 是否只读模式 */
  readonly?: boolean;
}

/** 从 sheetDataMap 中提取用于对比的核心数据（含 sheet.name 用于检测重命名变更） */
const extractSheetTableData = (map: TSheetDataMap, sheets: ISheet[]) => {
  if (!map || sheets.length === 0) return [];
  return sheets.map((sheet) => ({
    id: sheet.id,
    name: sheet.name,
    tableData: map.get(sheet.id)?.tableData || [],
  }));
};

/** 用于 usePageLifecycle diff 判断的哨兵值 */
const NO_CHANGE = 'no_change';

/**
 * 智能表格自动保存 Hook
 *
 * 通过 lodash isEqual 深度对比判断数据是否变化，快照完全内部管理。
 *
 * 覆盖场景：
 * 1. 切换文件 / 切换 sheet 前 —— 外部调用 flush() 主动保存
 * 2. 页面刷新/关闭（beforeunload）—— usePageLifecycle 自动处理
 * 3. 组件卸载（关闭面板）—— usePageLifecycle 自动处理
 * 4. 30s 定时保存 —— usePageLifecycle 自动处理
 */
export const useAutoSave = (props: IUseAutoSaveProps) => {
  const {
    sheetDataMap,
    sheets,
    sheetName,
    currentFileName,
    currentFileCosUrl,
    readonly = false,
  } = props;

  const { putObject } = useCos();

  // 快照基准
  const snapshotRef = useRef<ReturnType<typeof extractSheetTableData>>([]);
  const snapshotInitializedRef = useRef(false);
  const isSavingRef = useRef(false);

  // 实时同步最新 props 到 ref，供 callback 闭包内使用
  const saveContextRef = useRef({ sheetDataMap, sheets, sheetName, currentFileName, currentFileCosUrl });
  useEffect(() => {
    saveContextRef.current = { sheetDataMap, sheets, sheetName, currentFileName, currentFileCosUrl };
  }, [sheetDataMap, sheets, sheetName, currentFileName, currentFileCosUrl]);

  // 当文件切换时（cosUrl 变化），重置快照以便重新初始化
  const prevCosUrlRef = useRef(currentFileCosUrl);
  useEffect(() => {
    if (prevCosUrlRef.current !== currentFileCosUrl) {
      prevCosUrlRef.current = currentFileCosUrl;
      snapshotRef.current = [];
      snapshotInitializedRef.current = false;
    }
  }, [currentFileCosUrl]);

  // 自动初始化快照（文件数据加载完成后）
  useEffect(() => {
    if (snapshotInitializedRef.current) return;
    // 必须有 cosUrl，确保文件数据已加载，避免在空白初始数据时过早建立快照
    if (!currentFileCosUrl) return;
    const currentData = extractSheetTableData(sheetDataMap, sheets);
    const hasData = currentData.some((s) => s.tableData.length > 0);
    if (hasData) {
      snapshotRef.current = cloneDeep(currentData);
      snapshotInitializedRef.current = true;
    }
  }, [sheetDataMap, sheets, currentFileCosUrl]);

  /** 执行保存：收集 sheet 数据 → 转 Excel → 上传 COS */
  const doSave = useCallback(async () => {
    if (readonly || isSavingRef.current) return;
    // 快照未初始化时跳过（数据尚未加载 或 已被 flush 重置）
    if (!snapshotInitializedRef.current) return;

    const ctx = saveContextRef.current;
    const { sheetDataMap: map, sheets: sheetList, currentFileName: fileName, currentFileCosUrl: cosUrl } = ctx;
    if (!cosUrl || sheetList.length === 0) return;

    // diff 对比：无变化则跳过
    const currentData = extractSheetTableData(map, sheetList);
    if (isEqual(currentData, snapshotRef.current)) {
      return;
    }

    isSavingRef.current = true;
    try {
      const allSheetsData: IMultiSheetData[] = [];
      sheetList.forEach((sheet) => {
        const sheetData = map.get(sheet.id);
        allSheetsData.push({
          sheetName: sheet.name,
          tableData: sheetData?.tableData || [],
          columns: sheetData?.columns || [],
        });
      });

      if (allSheetsData.length === 0) return;

      const baseName = fileName || `${ctx.sheetName}.xlsx`;
      const finalFileName = baseName.endsWith('.xlsx') ? baseName : `${baseName}.xlsx`;
      const excelFile = await multiSheetDataToExcelFile(allSheetsData, finalFileName);
      const filePath = cosUrl.replace(/^https?:\/\/[^/]+/, '') || `/databrain_agent/test/user_file/${finalFileName}`;

      await putObject({ path: filePath, file: excelFile });

      // 保存成功后更新快照基准
      snapshotRef.current = cloneDeep(currentData);
    } catch (error) {
      console.error('[AutoSave] 自动保存失败:', error);
    } finally {
      isSavingRef.current = false;
    }
  }, [readonly, putObject]);

  /**
   * flush —— 立即保存当前数据并重建快照
   * 在切换文件/sheet 之前由外部调用，确保旧数据在被替换前完成保存。
   */
  const flush = useCallback(async () => {
    await doSave();
    // 重建快照（切换后新数据加载完成时，会由 useEffect 自动初始化新快照）
    snapshotRef.current = [];
    snapshotInitializedRef.current = false;
  }, [doSave]);

  // isDiffRender 对象：供 usePageLifecycle 判断数据是否有变化
  const diffRenderRef = useRef({
    getMarkdown: () => {
      const ctx = saveContextRef.current;
      const currentData = extractSheetTableData(ctx.sheetDataMap, ctx.sheets);
      return isEqual(currentData, snapshotRef.current) ? NO_CHANGE : String(Date.now());
    },
  });

  // usePageLifecycle 处理：定时保存 / 页面刷新 / 组件卸载
  // 包裹一层箭头函数以避免 @typescript-eslint/no-misused-promises：
  // doSave 是 async（返回 Promise），而 usePageLifecycle 的 callback 要求 () => void
  usePageLifecycle({
    value: NO_CHANGE,
    isDiffRender: diffRenderRef.current,
    callback: () => {
      doSave();
    },
  });

  return { flush };
};

export default useAutoSave;
